// Meteor Shower — creative timed event (Agent B), built on the space-storm
// timed-event mechanic. Meteors streak down at seeded-random arena positions
// (biased toward live enemies so the event actually connects), each with a
// 0.75 s telegraph; impacts damage every enemy in a small radius and can
// leave XP motes or a health pickup behind.
//
// Determinism (GDD Appendix A §A.1): every roll comes from a private
// mulberry32 stream reseeded from world.runSeed in start() — the shared
// RngService streams (spawn/loot/ai/crit/vfx/audio) are never touched, so
// run determinism is unaffected by this event. Same pattern as wormhole.ts.
// No Math.random, no wall-clock.
//
// Perf: at most one spawn roll plus a bounded pending list (MAX_PENDING)
// per tick; impacts iterate the enemy pool (pool_index order) only on the
// tick they land. Comfortably inside the 60 fps budget.
//
// Visuals (wired by the parent, not here): listen for the emitted bus events —
//   'hazard' { event: 'meteor_shower', phase: 'telegraph', pos, radius, impact_tick }
//   'hazard' { event: 'meteor_shower', phase: 'impact', pos, radius, damage }
// Suggested look: streaking meteor trails during the telegraph window, a
// bright impact flash + shockwave ring on impact.

import type { Enemy, EnemyDef, Vec2 } from '../../types.js';
import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { RngStream } from '../rng.js';
import { enemyHpAtTime } from '../combat.js';
import { wrappedDelta } from '../collision.js';
import { foldIntoOldestMote } from '../kill.js';
import enemiesJson from '../../content/enemies.json';

// GameEventDef is the canonical contract from Agent A's EventsManager
// (src/sim/events-manager.ts): { id, name, duration (sim ticks at 60 Hz),
// start(world, tick), update(world, tick, progress 0..1), end(world, tick) }.

const ENEMY_DEFS = (enemiesJson as { version: string; entries: EnemyDef[] }).entries;

/** Event lifetime: 12 s at 60 Hz. */
const EVENT_DURATION_TICKS = 12 * 60;
/** Warning time between a meteor's telegraph and its impact (0.75 s). */
const TELEGRAPH_TICKS = 45;
/** First meteor lands shortly after the event starts (0.5 s). */
const FIRST_METEOR_DELAY_TICKS = 30;
/** AoE radius of an impact (arena units). */
const IMPACT_RADIUS = 2.5;
/**
 * Impact damage base, scaled with the same 1.6^(t/120) curve as enemy HP
 * (enemyHpAtTime): one-shots base driftfin (12 hp) at every point in the run
 * and dents tougher enemies (void-fiend 30 hp) without trivializing them.
 */
const IMPACT_DAMAGE_BASE = 24;
/** Ticks between meteor spawns: uniform in [18, 30] (3.3–5 Hz feel). */
const MIN_SPAWN_INTERVAL_TICKS = 18;
const SPAWN_INTERVAL_SPAN_TICKS = 13; // next_int(13) → 0..12 added to the min
/** Bounded pending-telegraph list; spawns wait when full (never reached in practice). */
const MAX_PENDING = 16;
/** Fraction of meteors aimed at a random live enemy; the rest fall anywhere. */
const AIMED_FRACTION = 0.7;
/** Per-impact bonus drops (rolled on the private stream, independent of kills). */
const XP_DROP_CHANCE = 0.3;
const HEALTH_DROP_CHANCE = 0.12; // evaluated after the XP roll misses
const XP_DROP_COUNT = 2;
const HEALTH_PICKUP_VALUE = 15;
/** Placement margin kept inside the ±100 arena walls. */
const ARENA_MARGIN = 4;
/** Private stream salt: keeps meteor rolls independent of every other system. */
const METEOR_STREAM_SALT = 0x5A17C3D1;

interface PendingMeteor {
  x: number;
  y: number;
  impactTick: number;
}

function spawnXpMote(world: WorldState, pos: Vec2, value: number, tick: number): void {
  const p = world.pickups.acquire();
  if (!p) {
    // Pool exhausted: fold the value into the oldest live mote (see kill.ts).
    foldIntoOldestMote(world, value);
    return;
  }
  p.pickup_kind = 'xp_mote';
  p.value = value;
  p.pos = { x: pos.x, y: pos.y };
  p.vel = { x: 0, y: 0 };
  p.hp = 1;
  p.max_hp = 1;
  p.alive = true;
  p.spawn_tick = tick;
  p.targetable = false;
  p.magnet = true;
  world.events.emit('spawn', { kind: 'pickup', pickup_kind: 'xp_mote', pos: p.pos, value }, tick);
}

function spawnHealthPickup(world: WorldState, pos: Vec2, value: number, tick: number): void {
  const p = world.pickups.acquire();
  if (!p) return;
  p.pickup_kind = 'health';
  p.value = value;
  p.pos = { x: pos.x, y: pos.y };
  p.vel = { x: 0, y: 0 };
  p.hp = 1;
  p.max_hp = 1;
  p.alive = true;
  p.spawn_tick = tick;
  p.targetable = false;
  p.magnet = true;
  world.events.emit('spawn', { kind: 'pickup', pickup_kind: 'health', pos: p.pos, value }, tick);
}

/** XP mote value for a kill, mirroring enemy-ai's loot pass. */
function xpValueFor(defId: string): number {
  const def = ENEMY_DEFS.find((d) => d.id === defId);
  return def ? def.drops.xp_motes : 1;
}

/**
 * Create the Meteor Shower event def. All mutable state lives in this
 * closure and is fully reset in start(), so a def instance is reusable
 * across runs and across multiple triggers within one run.
 */
export function createMeteorShowerEvent(): GameEventDef {
  // Preallocated pending list (objects reused across runs; no per-tick alloc).
  const pending: PendingMeteor[] = [];
  for (let i = 0; i < MAX_PENDING; i++) {
    pending.push({ x: 0, y: 0, impactTick: 0 });
  }
  let pendingCount = 0;
  let nextSpawnTick = 0;
  let stream = new RngStream(0);

  function pickTargetPos(world: WorldState): Vec2 {
    const half = 100 - ARENA_MARGIN; // arena half-size (sim.arena_size 200)
    const aimed = stream.next() < AIMED_FRACTION;
    if (aimed && world.enemies.aliveCount > 0) {
      // k-th enemy in pool_index order (deterministic selection).
      const k = stream.next_int(world.enemies.aliveCount);
      let i = 0;
      for (const e of world.enemies.alive()) {
        if (i === k) return { x: e.pos.x, y: e.pos.y };
        i++;
      }
    }
    return { x: stream.next_range(-half, half), y: stream.next_range(-half, half) };
  }

  function resolveImpact(world: WorldState, m: PendingMeteor, tick: number): void {
    const pos = { x: m.x, y: m.y };
    const damage = enemyHpAtTime(IMPACT_DAMAGE_BASE, world.runTime);
    world.events.emit(
      'hazard',
      { event: 'meteor_shower', phase: 'impact', pos, radius: IMPACT_RADIUS, damage },
      tick
    );

    // AoE damage: collect victims in pool_index order first, then apply
    // (two-pass, same pattern as space-storm.ts — releasing inside the
    // pool generator is legal but harder to reason about).
    const victims: Enemy[] = [];
    for (const e of world.enemies.alive()) {
      if (!e.targetable) continue; // still materializing: not fully there yet
      const d = wrappedDelta(e.pos, pos);
      const r = IMPACT_RADIUS + e.hitbox_radius;
      if (d.x * d.x + d.y * d.y <= r * r) victims.push(e);
    }
    for (const e of victims) {
      e.hp -= damage;
      world.events.emit(
        'hit',
        {
          target_id: e.id,
          damage,
          crit: false,
          weapon_id: 'meteor_shower',
          pos: { x: e.pos.x, y: e.pos.y }
        },
        tick
      );

      if (e.hp <= 0) {
        world.events.emit(
          'death',
          {
            entity_id: e.id,
            enemy_id: e.id,
            kind: 'enemy',
            def_id: e.def_id,
            pos: { x: e.pos.x, y: e.pos.y }
          },
          tick
        );
        spawnXpMote(world, e.pos, xpValueFor(e.def_id), tick);
        world.enemies.release(e);
      }
    }

    // Bonus site drops, independent of kills: XP cluster or a health pickup.
    const roll = stream.next();
    if (roll < XP_DROP_CHANCE) {
      for (let i = 0; i < XP_DROP_COUNT; i++) spawnXpMote(world, pos, 1, tick);
    } else if (roll < XP_DROP_CHANCE + HEALTH_DROP_CHANCE) {
      spawnHealthPickup(world, pos, HEALTH_PICKUP_VALUE, tick);
    }
  }

  return {
    id: 'meteor_shower',
    name: 'Meteor Shower',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      stream = new RngStream((world.runSeed ^ METEOR_STREAM_SALT) >>> 0);
      pendingCount = 0;
      nextSpawnTick = tick + FIRST_METEOR_DELAY_TICKS;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'meteor_shower', phase: 'start', duration: EVENT_DURATION_TICKS },
        tick
      );
    },

    update(world: WorldState, tick: number, _progress: number): void {
      // Spawn the next telegraphed meteor when due.
      if (tick >= nextSpawnTick && pendingCount < MAX_PENDING) {
        const pos = pickTargetPos(world);
        const m = pending[pendingCount++];
        m.x = pos.x;
        m.y = pos.y;
        m.impactTick = tick + TELEGRAPH_TICKS;
        nextSpawnTick = tick + MIN_SPAWN_INTERVAL_TICKS + stream.next_int(SPAWN_INTERVAL_SPAN_TICKS);
        world.events.emit(
          'hazard',
          {
            event: 'meteor_shower',
            phase: 'telegraph',
            pos: { x: m.x, y: m.y },
            radius: IMPACT_RADIUS,
            impact_tick: m.impactTick
          },
          tick
        );
      }

      // Resolve due impacts; compact the pending list in place (spawn order
      // is preserved, so resolution order is deterministic).
      let write = 0;
      for (let read = 0; read < pendingCount; read++) {
        const m = pending[read];
        if (tick >= m.impactTick) {
          resolveImpact(world, m, tick);
        } else {
          pending[write++] = m;
        }
      }
      pendingCount = write;
    },

    end(world: WorldState, tick: number): void {
      // Final salvo: any meteor already telegraphed still lands, so a
      // telegraph never lies. Then the event is fully inert. Skipped when
      // the run just ended (the manager calls end() immediately on game
      // over) — no post-mortem kills feeding the score.
      if (!world.gameOver) {
        for (let i = 0; i < pendingCount; i++) {
          resolveImpact(world, pending[i], tick);
        }
      }
      pendingCount = 0;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'meteor_shower', phase: 'end' },
        tick
      );
    }
  };
}
